import * as React from "react";
import { useRouter } from "next/router";
import { removeMultiOrderItemsFromStorage } from "@nju/data-access-portal-delivery-address";
import { IMobileFlow, useOffers } from "@nju/data-access-portal-offers";
import { useOfferModeWithFsecure } from "@nju/data-access-portal-offers/src/utils";
import {
  IFSecureOrchestrator,
  IFtthOrchestrator,
  IMultiOrderOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { useMaintenance } from "@nju/data-access-portal-technical";
import { pushSimpleEvent } from "@nju/data-layer";
import { redirectToOrchestratorState } from "@nju/feature-portal-orchestrator";
import { portalRoutes } from "@nju/routes";
import { getAvailableActions } from "../utils";
import { Button, Link } from "./Pressable";

interface IButtonsProps {
  orchestrator:
    | IFtthOrchestrator
    | IMultiOrderOrchestrator
    | IFSecureOrchestrator;
  refreshCallback: () => void;
  handleConfirmMigration: () => void;
  handleCancelFailedMigration: () => void;
  isConfirmMigrationLoading: boolean;
  isCancelFailedMigrationLoading: boolean;
  flow: IMobileFlow | undefined;
}

export function Buttons({
  handleCancelFailedMigration,
  isCancelFailedMigrationLoading,
  orchestrator,
  refreshCallback,
  flow,
}: IButtonsProps) {
  const { paymentsUnavailable, orderUnavailable } = useMaintenance();
  const router = useRouter();
  const mode = useOfferModeWithFsecure();
  const { mobile: mobileOffers, postpaid: postpaidOffers } = useOffers();
  const offers = mode === "postpaid" ? postpaidOffers : mobileOffers;
  const offer = offers.indefiniteOffers.find((offer) => {
    return offer.display;
  });

  const {
    ORDER_CANCEL,
    PAYMENT_METHOD_SET,
    APPOINTMENT_SLOT_RESERVE,
    ORDER_COMMIT,
    CONSENTS_SET,
    AGREEMENT_DATA_SET,
  } = getAvailableActions(orchestrator.actions);

  const handlePushCancelOrderDataLayer = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_resignation_complete_order",
    });
  }, []);

  const handleSelectPayment = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_payment_complete_order",
    });

    if (paymentsUnavailable) {
      router.replace(portalRoutes.trwajaPrace.get());
    } else {
      redirectToOrchestratorState({ state: orchestrator.state, mode });
    }
  }, [paymentsUnavailable, router, orchestrator.state, mode]);

  const handleSelectAppointmentDate = React.useCallback(() => {
    if (orderUnavailable) {
      router.replace(portalRoutes.trwajaPrace.get());
    } else {
      redirectToOrchestratorState({ state: orchestrator.state, mode });
    }
  }, [orderUnavailable, router, orchestrator.state, mode]);

  const openChat = React.useCallback(() => {
    if ("closer" in window) {
      // @ts-expect-error missing types
      closer.openWidget();
      pushSimpleEvent({
        event: "activity_chat",
        eventName: "chat_open",
      });
    }
  }, []);

  const handleOrderCommit = React.useCallback(() => {
    if (orderUnavailable) {
      router.replace(portalRoutes.trwajaPrace.get());
    } else {
      redirectToOrchestratorState({
        state: orchestrator.state,
        mode,
        flow: "multiOrder",
      });
    }
  }, [orderUnavailable, router, orchestrator.state, mode]);

  const handleRedirectToOrchestratorState = React.useCallback(() => {
    redirectToOrchestratorState({
      state: orchestrator.state,
      mode,
      flow: "multiOrder",
    });
  }, [mode, orchestrator.state]);

  const handleCancelOrder = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_resignation_complete_order",
    });

    if (orderUnavailable) {
      router.replace(portalRoutes.trwajaPrace.get());
    } else {
      router.push(
        portalRoutes.mojeKonto.anulujZamowienie.get({
          mode,
          ...(orchestrator?.type === "NJU_MULTIORDER_ONBOARDING" &&
            flow && { flow }),
        }),
      );
    }
  }, [flow, mode, orchestrator?.type, orderUnavailable, router]);

  const selectedFailedItem =
    orchestrator.type === "NJU_MULTIORDER_ONBOARDING" &&
    orchestrator?.multiOrderItems?.find((item) => {
      return item.legacy?.feasibilityCode;
    })?.legacy?.feasibilityCode;

  if (orchestrator.state === "CONTRACT_VERIFICATION") {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Link
            href={portalRoutes.mojeKonto.anulujZamowienie.get({
              mode,
            })}
            onClick={handlePushCancelOrderDataLayer}
          >
            anuluj zamówienie
          </Link>
        )}
        <Button isPrimary={true} onPress={handleRedirectToOrchestratorState}>
          potwierdź numer telefonu
        </Button>
      </div>
    );
  }

  if (orchestrator.state === "IDENTITY") {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Link
            href={portalRoutes.mojeKonto.anulujZamowienie.get({
              mode,
              ...(orchestrator?.type === "NJU_MULTIORDER_ONBOARDING" &&
                flow && { flow }),
            })}
            onClick={handlePushCancelOrderDataLayer}
          >
            anuluj zamówienie
          </Link>
        )}
        <Button isPrimary={true} onPress={handleRedirectToOrchestratorState}>
          potwierdź tożsamość
        </Button>
      </div>
    );
  }

  if (orchestrator.state === "DELIVERY" && orchestrator.offerId) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Link
            href={portalRoutes.mojeKonto.anulujZamowienie.get({
              mode,
              ...(orchestrator?.type === "NJU_MULTIORDER_ONBOARDING" &&
                flow && { flow }),
            })}
            onClick={() => {
              removeMultiOrderItemsFromStorage();
              handlePushCancelOrderDataLayer();
            }}
          >
            anuluj zamówienie
          </Link>
        )}
        <Button isPrimary={true} onPress={handleRedirectToOrchestratorState}>
          dokończ zamówienie
        </Button>
      </div>
    );
  }

  if (orchestrator.state === "MNP") {
    return (
      <Button isPrimary={true} onPress={handleRedirectToOrchestratorState}>
        dalej
      </Button>
    );
  }

  if (orchestrator.state === "LEGACY_FEASIBILITY_PENDING") {
    return (
      <Button isPrimary={true} onPress={refreshCallback}>
        odśwież
      </Button>
    );
  }

  if (selectedFailedItem && orchestrator.state === "LEGACY_MIGRATION_ERROR") {
    if (["RC001", "RC003"].includes(selectedFailedItem)) {
      return (
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Button
            isLoading={isCancelFailedMigrationLoading}
            isPrimary={false}
            onPress={handleCancelFailedMigration}
          >
            anuluj zamówienie
          </Button>
          <Link
            href={portalRoutes.idziemyDalej.get({
              flow: "multiOrder",
              screenVariant: "failed",
            })}
            isPrimary={true}
          >
            przejdź dalej
          </Link>
        </div>
      );
    }

    if (
      [
        "RC002",
        "RC004",
        "RC005",
        "RC006",
        "RC007",
        "RC008",
        "RC009",
        "RC011",
        "RC012",
      ].includes(selectedFailedItem)
    ) {
      return (
        <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
          <Button
            isLoading={isCancelFailedMigrationLoading}
            isPrimary={false}
            onPress={handleCancelFailedMigration}
          >
            anuluj zamówienie
          </Button>
          <Button
            isPrimary={true}
            onPress={openChat}
            variant="neutral"
            withUnderline={true}
          >
            przejdź do czatu
          </Button>
          <Link
            href={portalRoutes.idziemyDalej.get({
              flow: "multiOrder",
              screenVariant: "failed",
            })}
            isPrimary={true}
          >
            przejdź dalej
          </Link>
        </div>
      );
    }
  }
  if (
    orchestrator.state === "LEGACY_MIGRATION_PENDING" ||
    orchestrator.state === "LEGACY_MIGRATION"
  ) {
    return (
      <Button isPrimary={true} onPress={handleRedirectToOrchestratorState}>
        dalej
      </Button>
    );
  }

  if (orchestrator.state === "IDENTITY_ERROR" && orchestrator.offerId) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Link
            href={portalRoutes.mojeKonto.anulujZamowienie.get({
              mode,
              ...(orchestrator?.type === "NJU_MULTIORDER_ONBOARDING" &&
                flow && { flow }),
            })}
            onClick={handlePushCancelOrderDataLayer}
          >
            anuluj zamówienie
          </Link>
        )}
        <Link
          href={portalRoutes.weryfikacjaTozsamosci.index.get({
            mode,
            oferta: orchestrator.offerId,
          })}
          isPrimary={true}
        >
          spróbuj ponownie
        </Link>
      </div>
    );
  }

  if (
    orchestrator?.type === "NJU_MULTIORDER_ONBOARDING" &&
    orchestrator.state === "ELIGIBILITY_ERROR"
  ) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        <Button
          isPrimary={true}
          onPress={handleCancelOrder}
          testId="cancel-order-button-on-eligibility-error-state"
        >
          zobacz ofertę nju subskrypcja
        </Button>
      </div>
    );
  }

  if (orchestrator.state === "PAYMENT_METHOD") {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Link
            href={portalRoutes.mojeKonto.anulujZamowienie.get({
              mode,
            })}
            onClick={handlePushCancelOrderDataLayer}
          >
            anuluj zamówienie
          </Link>
        )}
        {PAYMENT_METHOD_SET && (
          <Button isPrimary={true} onPress={handleSelectPayment}>
            wybierz sposób płatności
          </Button>
        )}
      </div>
    );
  }

  if (orchestrator.state === "IDENTITY_FULLSTOP") {
    return (
      <Button
        isPrimary={true}
        onPress={() => {
          if ("closer" in window) {
            // @ts-expect-error missing types
            closer.openWidget();
          }
        }}
      >
        przejdź do czatu
      </Button>
    );
  }

  if (orchestrator.state === "APPOINTMENT") {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Button
            onPress={handleCancelOrder}
            testId="cancel-order-button-on-appointment-state"
          >
            anuluj zamówienie
          </Button>
        )}
        {APPOINTMENT_SLOT_RESERVE && (
          <Button isPrimary={true} onPress={handleSelectAppointmentDate}>
            wybierz termin instalacji
          </Button>
        )}
      </div>
    );
  }

  if (
    orchestrator.state === "ORDER_SUMMARY" &&
    orchestrator.identityMethodId === "TECHNICALPARTNER" &&
    orchestrator.isUserVerified === false
  ) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Button onPress={handleCancelOrder}>anuluj zamówienie</Button>
        )}
        <Button isPrimary={true} onPress={handleRedirectToOrchestratorState}>
          dalej
        </Button>
      </div>
    );
  }

  if (
    orchestrator.state === "ORDER_SUMMARY" &&
    orchestrator.type === "NJU_FSECURE_ONBOARDING"
  ) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Button onPress={handleCancelOrder}>anuluj zamówienie</Button>
        )}
        <Button isPrimary={true} onPress={handleRedirectToOrchestratorState}>
          dalej
        </Button>
      </div>
    );
  }

  if (
    orchestrator.state === "ORDER_COMMITTED" &&
    orchestrator.type === "NJU_FSECURE_ONBOARDING"
  ) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        <Button isPrimary={true} onPress={refreshCallback}>
          odśwież
        </Button>
      </div>
    );
  }

  if (
    orchestrator.state === "ORDER_FULFILLMENT" &&
    orchestrator.type === "NJU_FSECURE_ONBOARDING"
  ) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        <Button isPrimary={true} onPress={refreshCallback}>
          odśwież
        </Button>
      </div>
    );
  }

  if (
    orchestrator.state === "ORDER_PAYMENT_ERROR" &&
    orchestrator.type === "NJU_FSECURE_ONBOARDING"
  ) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Button isPrimary={true} onPress={handleCancelOrder}>
            anuluj i zacznij od nowa
          </Button>
        )}
      </div>
    );
  }

  if (["ORDER_SUMMARY", "CONSENTS"].includes(orchestrator.state)) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Button
            onPress={handleCancelOrder}
            testId="cancel-order-button-on-summary-state"
          >
            anuluj zamówienie
          </Button>
        )}
        {(ORDER_COMMIT || CONSENTS_SET) && (
          <Button isPrimary={true} onPress={handleOrderCommit}>
            dalej
          </Button>
        )}
      </div>
    );
  }

  if (orchestrator.state === "AGREEMENT_DATA") {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Button
            onPress={handleCancelOrder}
            testId="cancel-order-button-on-agreement-data-state"
          >
            anuluj zamówienie
          </Button>
        )}
        {AGREEMENT_DATA_SET && (
          <Button isPrimary={true} onPress={handleRedirectToOrchestratorState}>
            dalej
          </Button>
        )}
      </div>
    );
  }

  if (orchestrator.state === "AGREEMENT") {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Button
            onPress={handleCancelOrder}
            testId="cancel-order-button-on-agreement-state"
          >
            anuluj zamówienie
          </Button>
        )}
        <Link href={portalRoutes.umowy.get({ mode })} isPrimary={true}>
          dalej
        </Link>
      </div>
    );
  }

  if (
    orchestrator.type !== "NJU_MULTIORDER_ONBOARDING" &&
    orchestrator.state === "ORDER_PENDING_CANCELLATION"
  ) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        <Button isPrimary={true} onPress={refreshCallback}>
          odśwież
        </Button>
      </div>
    );
  }

  if (orchestrator.state === "ITEM_SELECTION") {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        {ORDER_CANCEL && (
          <Link
            href={portalRoutes.mojeKonto.anulujZamowienie.get({
              mode,
              ...(orchestrator?.type === "NJU_MULTIORDER_ONBOARDING" &&
                flow && { flow }),
            })}
            onClick={handlePushCancelOrderDataLayer}
          >
            anuluj zamówienie
          </Link>
        )}
        <Link
          href={portalRoutes.czyZachowacNumer.get({
            mode,
            oferta: offer?.offerId,
          })}
          isPrimary={true}
        >
          dalej
        </Link>
      </div>
    );
  }

  if (
    orchestrator.state === "ORDER_CREATION_PENDING" ||
    orchestrator.state === "ORDER_CREATION_ERROR"
  ) {
    return (
      <div className="flex flex-col-reverse gap-3 from-tablet:flex-row">
        <Button isPrimary={true} onPress={refreshCallback}>
          odśwież
        </Button>
      </div>
    );
  }

  // eslint-disable-next-line unicorn/no-null
  return null;
}
